import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  TextField,
  Button,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  TablePagination,
  LinearProgress,
  Alert,
  Chip,
  MenuItem,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  Avatar,
  Rating,
} from '@mui/material';
import {
  MdSearch,
  MdRefresh,
  MdInfoOutline,
  MdFileDownload,
  MdGridOn,
  MdPrint,
  MdInventory2,
  MdShoppingBag,
  MdCurrencyRupee,
  MdEmojiEvents,
  MdRemoveShoppingCart,
  MdAssignmentReturn,
  MdTrendingUp,
} from 'react-icons/md';
import { getData } from '../../Webservice/Webservice';
import {
  FONT,
  inr,
  fmtDate,
  toISO,
  authToken,
  PRESETS,
  presetRange,
  rangeLabel,
  StatCard,
  statGridSx,
  exportButtonSx,
  downloadCsv,
  downloadExcel,
  openPrintView,
} from './reportShared';

const REPORT_ROUTE = 'reports/product-sales';
const pill = (bg, fg) => ({ fontWeight: 700, fontFamily: FONT, background: bg, color: fg, height: 24, fontSize: 12 });
const cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : '');
const dash = <Typography component="span" sx={{ color: '#94a3b8' }}>—</Typography>;

const returnChip = (rate, units) => {
  if (!units) return dash;
  const [bg, fg] = rate >= 10 ? ['#fee2e2', '#b91c1c'] : rate >= 5 ? ['#ffedd5', '#9a3412'] : ['#f1f5f9', '#475569'];
  return <Chip size="small" label={`${units} · ${rate}%`} sx={pill(bg, fg)} />;
};

const coverChip = (days) => {
  if (days === null || days === undefined) return <Chip size="small" label="No sales" sx={pill('#f1f5f9', '#64748b')} />;
  const [bg, fg] = days < 7 ? ['#fee2e2', '#b91c1c'] : days < 15 ? ['#fef3c7', '#92400e'] : ['#dcfce7', '#166534'];
  return <Chip size="small" label={`${days} day${days === 1 ? '' : 's'}`} sx={pill(bg, fg)} />;
};

const ProductCell = ({ r }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 220 }}>
    <Avatar variant="rounded" src={r.image || undefined} alt={r.title} sx={{ width: 40, height: 40, bgcolor: '#e2e8f0', color: '#475569', fontWeight: 700, fontSize: 15 }}>
      {(r.title || '?').charAt(0).toUpperCase()}
    </Avatar>
    <Box sx={{ lineHeight: 1.25, minWidth: 0 }}>
      <Typography sx={{ fontSize: 13, fontWeight: 700, fontFamily: FONT, maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.title}</Typography>
      <Typography sx={{ fontSize: 11.5, color: '#64748b', fontFamily: FONT }}>{[r.brand, r.productCode].filter(Boolean).join(' · ') || '—'}</Typography>
    </Box>
  </Box>
);

const lastSoldText = (r) => (r.lastSoldAt ? `${fmtDate(r.lastSoldAt)} (${r.daysSinceLastSale}d ago)` : 'Never sold');

// key/label drive the header; cell() is the on-screen render, text() the export/print value.
const col = (key, label, opts = {}) => ({ key, label, ...opts });
const TABS = [
  {
    key: 'best',
    label: 'Best Sellers',
    defaultSort: 'units_desc',
    sorts: [
      ['units_desc', 'Units sold: high to low'],
      ['units_asc', 'Units sold: low to high'],
      ['revenue_desc', 'Revenue: high to low'],
      ['revenue_asc', 'Revenue: low to high'],
      ['margin_desc', 'Margin: high to low'],
      ['margin_asc', 'Margin: low to high'],
      ['return_desc', 'Return rate: high to low'],
      ['cover_asc', 'Stock cover: lowest first'],
    ],
    columns: [
      col('rank', '#', { center: true, cell: (r, i) => i, text: (r, i) => i }),
      col('product', 'Product', { cell: (r) => <ProductCell r={r} />, text: (r) => r.title, extra: (r) => ({ Brand: r.brand, Code: r.productCode }) }),
      col('category', 'Category', { text: (r) => r.category || '-' }),
      col('unitsSold', 'Units Sold', { numeric: true, bold: true, text: (r) => r.unitsSold }),
      col('orders', 'Orders', { numeric: true, text: (r) => r.orders }),
      col('revenue', 'Revenue', { numeric: true, bold: true, text: (r) => r.revenue, cell: (r) => inr(r.revenue), print: (r) => inr(r.revenue) }),
      col('avgPrice', 'Avg Price', { numeric: true, text: (r) => r.avgPrice, cell: (r) => inr(r.avgPrice), print: (r) => inr(r.avgPrice) }),
      col('returns', 'Returned', { center: true, text: (r) => `${r.returnedUnits} (${r.returnRate}%)`, cell: (r) => returnChip(r.returnRate, r.returnedUnits) }),
      col('margin', 'Est. Profit / Margin', {
        numeric: true,
        text: (r) => (r.profit === null ? '' : `${r.profit} (${r.marginPct}%)`),
        cell: (r) =>
          r.profit === null ? (
            dash
          ) : (
            <Box sx={{ lineHeight: 1.25, color: r.profit >= 0 ? '#15803d' : '#b91c1c', fontWeight: 700 }}>
              {inr(r.profit)}
              <Typography component="div" sx={{ fontSize: 11.5, fontWeight: 600, fontFamily: FONT }}>{r.marginPct}%</Typography>
            </Box>
          ),
        print: (r) => (r.profit === null ? '-' : `${inr(r.profit)} (${r.marginPct}%)`),
      }),
      col('stock', 'Stock', { numeric: true, text: (r) => r.stock ?? '' }),
      col('stockCoverDays', 'Stock Cover', { center: true, text: (r) => (r.stockCoverDays === null ? 'No sales' : `${r.stockCoverDays} days`), cell: (r) => coverChip(r.stockCoverDays) }),
    ],
  },
  {
    key: 'slow',
    label: 'Slow / No Sales',
    defaultSort: 'stockvalue_desc',
    sorts: [
      ['stockvalue_desc', 'Stock value: high to low'],
      ['stock_desc', 'Stock: high to low'],
      ['idle_desc', 'Longest since last sale'],
      ['units_asc', 'Units sold: low to high'],
    ],
    columns: [
      col('product', 'Product', { cell: (r) => <ProductCell r={r} />, text: (r) => r.title, extra: (r) => ({ Brand: r.brand, Code: r.productCode }) }),
      col('category', 'Category', { text: (r) => r.category || '-' }),
      col('unitsSold', 'Units Sold (period)', { numeric: true, bold: true, text: (r) => r.unitsSold }),
      col('lastSoldAt', 'Last Sold', {
        text: lastSoldText,
        cell: (r) => (r.lastSoldAt ? lastSoldText(r) : <Chip size="small" label="Never sold" sx={pill('#fee2e2', '#b91c1c')} />),
      }),
      col('stock', 'Stock', { numeric: true, text: (r) => r.stock }),
      col('price', 'Selling Price', { numeric: true, text: (r) => r.price, cell: (r) => inr(r.price), print: (r) => inr(r.price) }),
      col('stockValue', 'Stock Value', { numeric: true, bold: true, text: (r) => r.stockValue, cell: (r) => inr(r.stockValue), print: (r) => inr(r.stockValue) }),
    ],
  },
  {
    key: 'returned',
    label: 'Most Returned',
    defaultSort: 'rate_desc',
    sorts: [
      ['rate_desc', 'Return rate: high to low'],
      ['returned_desc', 'Returned units: high to low'],
      ['sold_desc', 'Units sold: high to low'],
    ],
    columns: [
      col('product', 'Product', { cell: (r) => <ProductCell r={r} />, text: (r) => r.title, extra: (r) => ({ Brand: r.brand, Code: r.productCode }) }),
      col('category', 'Category', { text: (r) => r.category || '-' }),
      col('grossUnits', 'Units Sold', { numeric: true, text: (r) => r.grossUnits }),
      col('returnedUnits', 'Units Returned', { numeric: true, bold: true, text: (r) => r.returnedUnits }),
      col('returnRate', 'Return Rate', { center: true, text: (r) => `${r.returnRate}%`, cell: (r) => returnChip(r.returnRate, r.returnedUnits) }),
      col('topReason', 'Top Reason', { text: (r) => cap(r.topReason) || '-' }),
      col('stock', 'Stock', { numeric: true, text: (r) => r.stock ?? '' }),
    ],
  },
  {
    key: 'wishlisted',
    label: 'Most Wishlisted',
    defaultSort: 'wishlist_desc',
    sorts: [
      ['wishlist_desc', 'Wishlists: high to low'],
      ['sold_asc', 'Units sold: low to high'],
    ],
    columns: [
      col('product', 'Product', { cell: (r) => <ProductCell r={r} />, text: (r) => r.title, extra: (r) => ({ Brand: r.brand, Code: r.productCode }) }),
      col('category', 'Category', { text: (r) => r.category || '-' }),
      col('wishlistCount', 'Wishlisted By', { numeric: true, bold: true, text: (r) => r.wishlistCount }),
      col('unitsSold', 'Units Sold (period)', { numeric: true, text: (r) => r.unitsSold }),
      col('price', 'Selling Price', { numeric: true, text: (r) => r.price, cell: (r) => inr(r.price), print: (r) => inr(r.price) }),
      col('stock', 'Stock', { numeric: true, text: (r) => r.stock ?? '' }),
    ],
  },
  {
    key: 'reviewed',
    label: 'Most Reviewed',
    defaultSort: 'reviews_desc',
    sorts: [
      ['reviews_desc', 'Reviews: high to low'],
      ['rating_desc', 'Rating: high to low'],
      ['rating_asc', 'Rating: low to high'],
    ],
    columns: [
      col('product', 'Product', { cell: (r) => <ProductCell r={r} />, text: (r) => r.title, extra: (r) => ({ Brand: r.brand, Code: r.productCode }) }),
      col('category', 'Category', { text: (r) => r.category || '-' }),
      col('reviewCount', 'Reviews', { numeric: true, bold: true, text: (r) => r.reviewCount }),
      col('avgRating', 'Avg Rating', {
        text: (r) => r.avgRating,
        cell: (r) => (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Rating value={r.avgRating} precision={0.1} readOnly size="small" />
            <Typography sx={{ fontSize: 12.5, fontWeight: 700, fontFamily: FONT }}>{r.avgRating}</Typography>
          </Box>
        ),
      }),
      col('lowRatings', 'Low Ratings (≤2★)', {
        numeric: true,
        text: (r) => r.lowRatings,
        cell: (r) => (r.lowRatings ? <Chip size="small" label={r.lowRatings} sx={pill('#fee2e2', '#b91c1c')} /> : dash),
      }),
      col('unitsSold', 'Units Sold (period)', { numeric: true, text: (r) => r.unitsSold }),
    ],
  },
];

const ProductSalesReport = () => {
  const [tabIndex, setTabIndex] = useState(0);
  const tab = TABS[tabIndex];

  const [preset, setPreset] = useState('all');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('');
  const [brand, setBrand] = useState('');
  const [returnsMode, setReturnsMode] = useState('include');
  const [sortBy, setSortBy] = useState(TABS[0].defaultSort);
  const [maxUnits, setMaxUnits] = useState('2');
  const [stockMode, setStockMode] = useState('in');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);
  const [notesOpen, setNotesOpen] = useState(false);

  useEffect(() => {
    const id = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(0);
    }, 400);
    return () => clearTimeout(id);
  }, [searchInput]);

  const filterParams = useMemo(() => {
    const p = { tab: tab.key, sortBy, returns: returnsMode };
    if (from) p.from = from;
    if (to) p.to = to;
    if (search) p.search = search;
    if (category) p.category = category;
    if (brand) p.brand = brand;
    if (tab.key === 'slow') {
      p.maxUnits = maxUnits;
      p.stock = stockMode;
    }
    return p;
  }, [tab.key, sortBy, returnsMode, from, to, search, category, brand, maxUnits, stockMode]);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await getData(REPORT_ROUTE, { ...filterParams, page: page + 1, limit: rowsPerPage }, authToken(), false);
      if (res?.success) setData(res);
      else setError(res?.message || 'Failed to load report');
    } catch (err) {
      setError(err.message || 'Failed to load report');
    } finally {
      setLoading(false);
    }
  }, [filterParams, page, rowsPerPage]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  const goToTab = (index, overrides = {}) => {
    setTabIndex(index);
    setSortBy(TABS[index].defaultSort);
    setPage(0);
    if (overrides.maxUnits !== undefined) setMaxUnits(overrides.maxUnits);
    if (overrides.stockMode !== undefined) setStockMode(overrides.stockMode);
  };

  const applyPreset = (key) => {
    const range = presetRange(key);
    setPreset(key);
    setFrom(range.from);
    setTo(range.to);
    setPage(0);
  };

  const onDateChange = (setter) => (e) => {
    setter(e.target.value);
    setPreset('');
    setPage(0);
  };

  const setFilter = (setter) => (e) => {
    setter(e.target.value);
    setPage(0);
  };

  const resetFilters = () => {
    setPreset('all');
    setFrom('');
    setTo('');
    setSearchInput('');
    setSearch('');
    setCategory('');
    setBrand('');
    setReturnsMode('include');
    setSortBy(tab.defaultSort);
    setMaxUnits('2');
    setStockMode('in');
    setPage(0);
  };

  const exportColumns = tab.columns.filter((c) => c.key !== 'rank');

  const exportObjects = (rows) =>
    rows.map((r) => {
      const obj = {};
      for (const c of exportColumns) {
        obj[c.label] = c.text(r);
        if (c.extra) Object.assign(obj, c.extra(r));
      }
      return obj;
    });

  const fileStamp = () => `Product_Sales_${tab.label.replace(/[^A-Za-z]+/g, '_')}_${from || 'all'}_${to || toISO(new Date())}`;

  const runExport = async (kind) => {
    setExporting(true);
    try {
      const res = await getData(REPORT_ROUTE, { ...filterParams, export: 'all' }, authToken(), false);
      if (!res?.success) throw new Error(res?.message || 'Export failed');
      const rows = res.rows || [];
      if (kind === 'csv') downloadCsv(exportObjects(rows), fileStamp());
      else if (kind === 'excel') downloadExcel(exportObjects(rows), fileStamp(), tab.label);
      else
        openPrintView({
          title: `Product Sales Report — ${tab.label}`,
          subtitle: `${rangeLabel(from, to)} · ${rows.length} products`,
          headers: exportColumns.map((c) => c.label),
          cells: rows.map((r) => exportColumns.map((c) => ({ value: c.print ? c.print(r) : c.text(r), className: c.numeric ? 'num' : '' }))),
        });
    } catch (err) {
      setError(err.message || 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  const stats = data?.stats || {};
  const total = data?.meta?.total || 0;
  const rows = data?.tab === tab.key ? data.rows || [] : [];
  const filterOptions = data?.filterOptions || { categories: [], brands: [] };
  const baseIndex = page * rowsPerPage;

  const cards = [
    { label: 'Products Sold', value: (stats.productsSold || 0).toLocaleString('en-IN'), icon: MdShoppingBag, gradient: 'linear-gradient(135deg,#6366f1,#8b5cf6)', sub: `Over ${stats.periodDays || 0} day${stats.periodDays === 1 ? '' : 's'}` },
    { label: 'Units Sold', value: (stats.unitsSold || 0).toLocaleString('en-IN'), icon: MdInventory2, gradient: 'linear-gradient(135deg,#0ea5e9,#2563eb)', sub: returnsMode === 'exclude' ? 'Net of returns' : 'Returns included' },
    { label: 'Product Revenue', value: inr(stats.revenue), icon: MdCurrencyRupee, gradient: 'linear-gradient(135deg,#f59e0b,#ea580c)', sub: 'Incl. GST' },
    { label: 'Top Product', value: stats.topProduct?.title || '—', icon: MdEmojiEvents, gradient: 'linear-gradient(135deg,#10b981,#059669)', sub: stats.topProduct ? `${stats.topProduct.units} units` : 'No sales' },
    { label: 'No-Sale Products', value: (stats.noSaleProducts || 0).toLocaleString('en-IN'), icon: MdRemoveShoppingCart, gradient: 'linear-gradient(135deg,#64748b,#334155)', sub: 'Click to review', onClick: () => goToTab(1, { maxUnits: '0', stockMode: 'all' }), active: tab.key === 'slow' && maxUnits === '0' && stockMode === 'all' },
    { label: 'Return Rate', value: `${stats.returnRate || 0}%`, icon: MdAssignmentReturn, gradient: 'linear-gradient(135deg,#f43f5e,#dc2626)', sub: `${stats.returnedUnits || 0} units returned · click to review`, onClick: () => goToTab(2), active: tab.key === 'returned' },
    { label: 'Est. Margin', value: stats.marginPct === null || stats.marginPct === undefined ? '—' : `${stats.marginPct}%`, icon: MdTrendingUp, gradient: 'linear-gradient(135deg,#ec4899,#db2777)', sub: stats.marginPct === null || stats.marginPct === undefined ? 'No purchase cost recorded' : `Profit ${inr(stats.profit)}` },
  ];

  const activeFilterCount = [from || to, search, category, brand, returnsMode !== 'include', sortBy !== tab.defaultSort].filter(Boolean).length;

  return (
    <Box sx={{ p: { xs: 1, md: 3 }, fontFamily: FONT }}>
      {/* Header */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: 2, mb: 3 }}>
        <Box>
          <Typography sx={{ fontSize: 26, fontWeight: 800, fontFamily: FONT, color: '#0f172a' }}>Product Sales Report</Typography>
          <Typography sx={{ fontSize: 14, color: '#64748b', fontFamily: FONT }}>Best sellers, slow movers, returns, wishlist and reviews in one place</Typography>
        </Box>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          <Button variant="outlined" startIcon={<MdInfoOutline />} onClick={() => setNotesOpen(true)} sx={{ textTransform: 'none', fontWeight: 700, borderRadius: '10px', fontFamily: FONT }}>
            Note
          </Button>
          <Button startIcon={<MdFileDownload />} disabled={exporting || !total} onClick={() => runExport('csv')} sx={exportButtonSx('#16a34a', '#15803d')}>CSV</Button>
          <Button startIcon={<MdGridOn />} disabled={exporting || !total} onClick={() => runExport('excel')} sx={exportButtonSx('#0d9488', '#0f766e')}>Excel</Button>
          <Button startIcon={<MdPrint />} disabled={exporting || !total} onClick={() => runExport('print')} sx={exportButtonSx('#7c3aed', '#6d28d9')}>Print / PDF</Button>
        </Box>
      </Box>

      <Box sx={statGridSx}>
        {cards.map((c) => (
          <StatCard key={c.label} {...c} />
        ))}
      </Box>

      {/* Filters */}
      <Paper elevation={0} sx={{ p: 2.5, borderRadius: '16px', mb: 3, border: '1px solid #e2e8f0' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography sx={{ fontWeight: 800, fontFamily: FONT, color: '#0f172a' }}>
            Filters {activeFilterCount > 0 && <Chip size="small" label={`${activeFilterCount} active`} color="primary" sx={{ ml: 1, fontWeight: 700 }} />}
          </Typography>
          <Button size="small" startIcon={<MdRefresh />} onClick={resetFilters} sx={{ textTransform: 'none', fontWeight: 700, color: '#dc2626' }}>Reset</Button>
        </Box>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
          {PRESETS.map((p) => (
            <Chip
              key={p.key}
              label={p.label}
              onClick={() => applyPreset(p.key)}
              sx={{
                fontWeight: 700,
                fontFamily: FONT,
                background: preset === p.key ? 'rgb(255 209 2)' : '#f1f5f9',
                color: '#0f172a',
                border: preset === p.key ? '1px solid #0f172a' : '1px solid transparent',
                '&:hover': { background: preset === p.key ? 'rgb(255 209 2)' : '#e2e8f0' },
              }}
            />
          ))}
        </Box>

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3} lg={2}>
            <TextField label="From" type="date" size="small" fullWidth InputLabelProps={{ shrink: true }} value={from} onChange={onDateChange(setFrom)} inputProps={{ max: to || undefined }} />
          </Grid>
          <Grid item xs={12} sm={6} md={3} lg={2}>
            <TextField label="To" type="date" size="small" fullWidth InputLabelProps={{ shrink: true }} value={to} onChange={onDateChange(setTo)} inputProps={{ min: from || undefined }} />
          </Grid>
          <Grid item xs={12} sm={12} md={6} lg={3}>
            <TextField
              label="Search product name / code"
              size="small"
              fullWidth
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              InputProps={{ startAdornment: (<InputAdornment position="start"><MdSearch size={18} /></InputAdornment>) }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3} lg={2.5}>
            <TextField select label="Category" size="small" fullWidth value={category} onChange={setFilter(setCategory)} SelectProps={{ MenuProps: { PaperProps: { sx: { maxHeight: 320 } } } }}>
              <MenuItem value="">All categories</MenuItem>
              {filterOptions.categories.map((c) => (
                <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6} md={3} lg={2.5}>
            <TextField select label="Brand" size="small" fullWidth value={brand} onChange={setFilter(setBrand)} SelectProps={{ MenuProps: { PaperProps: { sx: { maxHeight: 320 } } } }}>
              <MenuItem value="">All brands</MenuItem>
              {filterOptions.brands.map((b) => (
                <MenuItem key={b} value={b}>{b}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <TextField select label="Sort by" size="small" fullWidth value={sortBy} onChange={setFilter(setSortBy)}>
              {tab.sorts.map(([value, label]) => (
                <MenuItem key={value} value={value}>{label}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <TextField select label="Returns" size="small" fullWidth value={returnsMode} onChange={setFilter(setReturnsMode)}>
              <MenuItem value="include">Include returned units</MenuItem>
              <MenuItem value="exclude">Net of returns</MenuItem>
            </TextField>
          </Grid>
          {tab.key === 'slow' && (
            <>
              <Grid item xs={12} sm={6} md={4} lg={3}>
                <TextField select label="Units sold in period" size="small" fullWidth value={maxUnits} onChange={setFilter(setMaxUnits)}>
                  <MenuItem value="0">No sales (0)</MenuItem>
                  <MenuItem value="1">1 or fewer</MenuItem>
                  <MenuItem value="2">2 or fewer</MenuItem>
                  <MenuItem value="5">5 or fewer</MenuItem>
                  <MenuItem value="10">10 or fewer</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6} md={4} lg={3}>
                <TextField select label="Stock" size="small" fullWidth value={stockMode} onChange={setFilter(setStockMode)}>
                  <MenuItem value="in">In stock only</MenuItem>
                  <MenuItem value="all">All products</MenuItem>
                </TextField>
              </Grid>
            </>
          )}
        </Grid>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {/* Tabs + table */}
      <Paper elevation={0} sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', overflow: 'hidden' }}>
        <Tabs
          value={tabIndex}
          onChange={(_, v) => goToTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            px: 1,
            borderBottom: '1px solid #e2e8f0',
            '& .MuiTab-root': { textTransform: 'none', fontWeight: 700, fontFamily: FONT, minHeight: 56 },
            '& .Mui-selected': { color: '#0f172a !important' },
            '& .MuiTabs-indicator': { background: 'rgb(255 209 2)', height: 4, borderRadius: 2 },
          }}
        >
          {TABS.map((t) => (
            <Tab key={t.key} label={t.label} />
          ))}
        </Tabs>
        <Box sx={{ px: 2.5, py: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
          <Typography sx={{ fontWeight: 800, fontFamily: FONT, color: '#0f172a' }}>{tab.label}</Typography>
          <Typography sx={{ fontSize: 13, color: '#64748b', fontFamily: FONT }}>
            {total.toLocaleString('en-IN')} products
            {tab.key === 'slow' && stats.slowStockValue !== undefined && <> · <b>{inr(stats.slowStockValue)}</b> stock value</>}
          </Typography>
        </Box>
        {(loading || exporting) && <LinearProgress />}
        <TableContainer sx={{ maxHeight: 600 }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                {tab.columns.map((c) => (
                  <TableCell
                    key={c.key}
                    align={c.center ? 'center' : c.numeric ? 'right' : 'left'}
                    sx={{ fontWeight: 800, fontFamily: FONT, background: '#0f172a', color: '#fff', whiteSpace: 'nowrap', py: 1.5 }}
                  >
                    {c.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {!loading && rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={tab.columns.length} sx={{ textAlign: 'center', color: '#94a3b8', py: 6, fontFamily: FONT }}>
                    No products match the selected filters.
                  </TableCell>
                </TableRow>
              )}
              {rows.map((r, idx) => (
                <TableRow key={`${r.productId || r.title}-${idx}`} hover sx={{ '&:nth-of-type(even)': { background: '#f8fafc' } }}>
                  {tab.columns.map((c) => (
                    <TableCell
                      key={c.key}
                      align={c.center ? 'center' : c.numeric ? 'right' : 'left'}
                      sx={{ fontFamily: FONT, whiteSpace: 'nowrap', fontSize: 13, fontWeight: c.bold ? 700 : 500, color: '#334155' }}
                    >
                      {c.cell ? c.cell(r, baseIndex + idx + 1) : c.text(r, baseIndex + idx + 1)}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={total}
          page={Math.min(page, Math.max(Math.ceil(total / rowsPerPage) - 1, 0))}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[10, 25, 50, 100]}
          sx={{ borderTop: '1px solid #e2e8f0' }}
        />
      </Paper>

      <Dialog open={notesOpen} onClose={() => setNotesOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 800, fontFamily: FONT }}>Note</DialogTitle>
        <DialogContent dividers>
          <Box component="ul" sx={{ pl: 2, m: 0 }}>
            {(data?.notes || []).map((n, i) => (
              <Typography component="li" key={i} variant="body2" sx={{ mb: 1, fontFamily: FONT }}>{n}</Typography>
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNotesOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProductSalesReport;
